import * as React from 'react';
import { useLazyQuery } from "@apollo/react-hooks";
import gql from "graphql-tag";
import { AsyncLoadResult, AsyncLoadOptions } from '../../../../shared/abstractStore';
import { questionnaireUsersAddSupportingDataQuery } from '../../generated/questionnaireUsersAddSupportingDataQuery';
import { mainApiConfig } from '../../../../configure/mainApiConfig';
import { subscriptionDepartmentFieldsFragment } from '../../models/SubscriptionDepartment';
import { profileFieldsFragment } from '../../models/Profile';
import { questionnaireFieldsFragment } from '../../models/Questionnaire';
import { questionnaireRespondentSessionFieldsFragment } from '../../models/QuestionnaireRespondentSession';

export interface QuestionnaireUsersAddSupportingDataAsyncLoadOptions extends AsyncLoadOptions {
    subscriptionId?: string | null | undefined,
    questionnaireId?: string | null | undefined,
}

/**
 * Supporting data for QuestionnaireUsersAdd that loads static data (e.g. dropdowns) that are not changed by the model being used.
 * @param options
 */
export function useQuestionnaireUsersAddSupportingData(options: QuestionnaireUsersAddSupportingDataAsyncLoadOptions = {}): AsyncLoadResult<questionnaireUsersAddSupportingDataQuery> {
    // Query the data we need form the api.
    const [load, { data, loading, error, refetch }] = useLazyQuery<questionnaireUsersAddSupportingDataQuery>(
        gql`
        query questionnaireUsersAddSupportingDataQuery ($subscriptionId: ID!, $questionnaireId: ID!) {
            subscriptionDepartments: subscriptionDepartments(subscriptionId: $subscriptionId) {
                ...subscriptionDepartmentFields
            }

            profiles: profiles(subscriptionId: $subscriptionId) {
                ...profileFields
            }

            questionnaire: questionnaire(id: $questionnaireId){
                ...questionnaireFields

                questionnaireRespondentSessions: questionnaireRespondentSessions(questionnaireId: $questionnaireId){
                    ...questionnaireRespondentSessionFields
                }
            }

        }

        ${subscriptionDepartmentFieldsFragment}
        ${profileFieldsFragment}
        ${questionnaireFieldsFragment}
        ${questionnaireRespondentSessionFieldsFragment}
        `,
        {
            variables: {
                subscriptionId: options.subscriptionId,
                questionnaireId: options.questionnaireId,
            },
            fetchPolicy: options.fetchPolicy ?? mainApiConfig.defaultFetchPolicy,
            pollInterval: options.pollInterval,
            onCompleted: (data) => {
                if (options.onCompleted) {
                    options.onCompleted(data);
                }
            }
        }
    );

    // Run the query (unless we are being lazy).
    React.useEffect(() => {
        if (!options.lazy) {
            load();
        }
    }, [options.lazy, load])

    // Return the results.
    const refresh = React.useCallback(async () => { !refetch ? await load() : await refetch(); }, [refetch, load]);
    return React.useMemo(() => ({
        data: data ?? ({} as any),
        refresh: refresh,
        isLoading: loading,
        errors: error
    }), [data, refresh, loading, error]);
}